import { buildPayment, type BuildContext } from './documents'
import { CoreError, assertSafeInt } from './errors'
import type { Paisa } from './money'
import { addOrderItem, openOrder, type OrderItemInput } from './orders'
import type { DeliveryRow, OrderItemRow, OrderRow, SyncOp, SyncRowBase } from './rows'

// Available in browsers, Workers and Node 19+; declared here so core needs no DOM/Node types.
declare const crypto: { randomUUID(): string }

// Delivery with the restaurant's own riders. A delivery order is an order with
// service_type 'delivery', a customer and no table; its delivery row says
// where it goes and what became of it. Dispatch checks the order out (a normal
// invoice, unpaid for cash on delivery) and sets the delivery out in one tx;
// the rider's cash is collected later as ordinary payments.

function base(ctx: BuildContext, id?: string): SyncRowBase {
  return {
    id: id ?? (ctx.newId ?? (() => crypto.randomUUID()))(),
    tenant_id: ctx.tenantId,
    created_at: ctx.now,
    updated_at: ctx.now,
    deleted_at: null,
  }
}

function op(table: string, row: object): SyncOp {
  return { table, op: 'upsert', row: { ...row } }
}

const text = (value: string | null | undefined) => value?.trim() || null

export interface OpenDeliveryInput {
  /** The device's next order number for the day. */
  orderNo: number
  customerId: string
  /** The name on the order (usually the customer's). */
  label?: string | null
  /** Who takes the order (see openOrder). */
  serverId?: string | null
  address: string
  phone?: string | null
  note?: string | null
  /** The delivery fee as an item of the delivery product (its price may be changed). */
  fee?: OrderItemInput | null
}

/** Opens a delivery order with its delivery row and, if given, the fee: one tx. */
export function openDelivery(
  ctx: BuildContext,
  input: OpenDeliveryInput,
): { order: OrderRow; delivery: DeliveryRow; items: OrderItemRow[]; ops: SyncOp[] } {
  if (!input.customerId) throw new CoreError('invalid_input', 'a delivery needs a customer')
  const address = text(input.address)
  if (!address) throw new CoreError('invalid_input', 'a delivery needs an address')
  const opened = openOrder(ctx, {
    tableId: null,
    serviceType: 'delivery',
    orderNo: input.orderNo,
    customerId: input.customerId,
    label: input.label,
    serverId: input.serverId,
  })
  const delivery: DeliveryRow = {
    ...base(ctx),
    order_id: opened.order.id,
    address,
    phone: text(input.phone),
    note: text(input.note),
    rider_name: null,
    status: 'pending',
    dispatched_at: null,
    delivered_at: null,
    failed_reason: null,
  }
  const ops = [...opened.ops, op('deliveries', delivery)]
  const items: OrderItemRow[] = []
  if (input.fee && input.fee.unitPricePaisa > 0) {
    const fee = addOrderItem(ctx, opened.order, { ...input.fee, station: 'none' })
    items.push(fee.item)
    ops.push(...fee.ops)
  }
  return { order: opened.order, delivery, items, ops }
}

function assertStatus(delivery: DeliveryRow, ...allowed: DeliveryRow['status'][]): void {
  if (!allowed.includes(delivery.status))
    throw new CoreError('invalid_input', `this delivery is already ${delivery.status}`)
}

/** Address, phone, note or rider, while the delivery has not left. */
export function updateDelivery(
  ctx: BuildContext,
  delivery: DeliveryRow,
  changes: Partial<Pick<DeliveryRow, 'address' | 'phone' | 'note' | 'rider_name'>>,
): { delivery: DeliveryRow; ops: SyncOp[] } {
  assertStatus(delivery, 'pending')
  const next: DeliveryRow = { ...delivery, updated_at: ctx.now }
  if (changes.address !== undefined) {
    const address = text(changes.address)
    if (!address) throw new CoreError('invalid_input', 'a delivery needs an address')
    next.address = address
  }
  if (changes.phone !== undefined) next.phone = text(changes.phone)
  if (changes.note !== undefined) next.note = text(changes.note)
  if (changes.rider_name !== undefined) next.rider_name = text(changes.rider_name)
  return { delivery: next, ops: [op('deliveries', next)] }
}

/**
 * The delivery leaves with its rider. The caller puts this op in the checkout
 * tx, after the op that closes the order (the server checks the order is closed).
 */
export function dispatchDelivery(
  ctx: BuildContext,
  delivery: DeliveryRow,
  riderName?: string | null,
): { delivery: DeliveryRow; ops: SyncOp[] } {
  assertStatus(delivery, 'pending')
  const next: DeliveryRow = {
    ...delivery,
    rider_name: riderName === undefined ? delivery.rider_name : text(riderName),
    status: 'out',
    dispatched_at: ctx.now,
    updated_at: ctx.now,
  }
  return { delivery: next, ops: [op('deliveries', next)] }
}

export function markDelivered(
  ctx: BuildContext,
  delivery: DeliveryRow,
): { delivery: DeliveryRow; ops: SyncOp[] } {
  assertStatus(delivery, 'out')
  const next: DeliveryRow = {
    ...delivery,
    status: 'delivered',
    delivered_at: ctx.now,
    updated_at: ctx.now,
  }
  return { delivery: next, ops: [op('deliveries', next)] }
}

/** The customer was not reached or refused it; a manager then cancels the invoice. */
export function markFailed(
  ctx: BuildContext,
  delivery: DeliveryRow,
  reason: string,
): { delivery: DeliveryRow; ops: SyncOp[] } {
  assertStatus(delivery, 'out')
  const why = text(reason)
  if (!why) throw new CoreError('invalid_input', 'say why the delivery failed')
  const next: DeliveryRow = {
    ...delivery,
    status: 'failed',
    failed_reason: why,
    updated_at: ctx.now,
  }
  return { delivery: next, ops: [op('deliveries', next)] }
}

/** A delivery that left, with what is still unpaid on its invoice. */
export interface RiderDelivery {
  delivery: DeliveryRow
  invoiceId: string
  customerId: string | null
  /** The invoice total less what has been paid on it. */
  duePaisa: Paisa
}

export interface RiderCash {
  /** Null: no rider was named. */
  rider: string | null
  duePaisa: Paisa
  deliveries: RiderDelivery[]
}

/**
 * Cash each rider holds: what is unpaid on the invoices of deliveries that are
 * out or delivered, by rider (riders by name, then the unnamed). Never stored.
 */
export function riderCash(entries: readonly RiderDelivery[]): RiderCash[] {
  const byRider = new Map<string | null, RiderCash>()
  for (const entry of entries) {
    const { status } = entry.delivery
    if ((status !== 'out' && status !== 'delivered') || entry.duePaisa <= 0) continue
    const rider = entry.delivery.rider_name
    const group = byRider.get(rider) ?? { rider, duePaisa: 0, deliveries: [] }
    group.duePaisa += entry.duePaisa
    group.deliveries.push(entry)
    byRider.set(rider, group)
  }
  return [...byRider.values()].sort((a, b) =>
    a.rider === null ? 1 : b.rider === null ? -1 : a.rider.localeCompare(b.rider),
  )
}

/**
 * The rider hands in the cash: one cash payment per invoice, and every
 * delivery still out marked delivered, in one tx.
 */
export function collectRiderCash(
  ctx: BuildContext,
  entries: readonly RiderDelivery[],
  paymentDate: string,
): { ops: SyncOp[]; totalPaisa: Paisa } {
  const ops: SyncOp[] = []
  let totalPaisa = 0
  for (const entry of entries) {
    if (entry.duePaisa > 0) {
      assertSafeInt(entry.duePaisa, 'duePaisa')
      ops.push(
        ...buildPayment(ctx, {
          customerId: entry.customerId,
          amountPaisa: entry.duePaisa,
          method: 'cash',
          paymentDate,
          notes: entry.delivery.rider_name ? `Delivery, ${entry.delivery.rider_name}` : 'Delivery',
          allocations: [{ invoiceId: entry.invoiceId, amountPaisa: entry.duePaisa }],
        }).ops,
      )
      totalPaisa += entry.duePaisa
    }
    if (entry.delivery.status === 'out') ops.push(...markDelivered(ctx, entry.delivery).ops)
  }
  if (ops.length === 0) throw new CoreError('invalid_input', 'nothing to collect')
  return { ops, totalPaisa }
}
